/**
 * Admin Token Refresh Composable
 * Handles admin access token refresh logic
 *
 * Follows DRY, SOLID principles
 */

import { computed, ref } from 'vue'
import { consola } from 'consola'
import { useAdminSessionManager } from './useAdminSessionManager'
import { apiAdminAuth } from '~/entities/auth'
import { navigateTo } from '#app'

const logger = consola.withTag('admin-token-refresh')

// Shared state для предотвращения параллельных refresh запросов
const isRefreshing = ref(false)
const refreshPromise = ref<Promise<string | null> | null>(null)

export const useAdminTokenRefresh = () => {
  const adminSessionManager = useAdminSessionManager()

  /**
   * Обновление админского access token
   * ✅ Защита от race conditions - только один запрос одновременно
   *
   * @returns Новый admin access token или null при ошибке
   */
  const refreshAdminToken = (): Promise<string | null> => {
    // Если уже идет обновление, возвращаем существующий promise
    if (isRefreshing.value && refreshPromise.value) {
      logger.info('⏳ Админский токен уже обновляется, ждем завершения')
      return refreshPromise.value
    }

    isRefreshing.value = true

    // Создаем новый promise для других запросов
    refreshPromise.value = (async () => {
      try {
        // Получаем refresh token
        const refreshToken = adminSessionManager.getAdminRefreshToken()

        if (!refreshToken) {
          logger.error('❌ Admin refresh token отсутствует')
          return null
        }

        logger.info('🔄 Обновление админского access token')

        // Вызываем API для обновления токенов
        const response = await apiAdminAuth.refresh(refreshToken)

        // Сохраняем новые токены
        adminSessionManager.updateAdminTokens(
          response.adminAccessToken,
          response.adminRefreshToken,
        )

        logger.success('✅ Админский токен успешно обновлен')
        return response.adminAccessToken

      } catch (error: any) {
        /* v8 ignore next */
        logger.error('❌ Ошибка обновления админского токена:', error?.data?.message || error?.message)

        // Если refresh token тоже невалиден, очищаем сессию
        if (error?.status === 401 || error?.statusCode === 401) {
          logger.error('❌ Admin refresh token недействителен, очищаем сессию')
          adminSessionManager.clearAdminSession()

          // Редирект на страницу авторизации админа
          /* v8 ignore next */
          if (process.client) {
            navigateTo('/administration/home')
          }
        }

        return null
      /* v8 ignore next 3 */
      } finally {
        isRefreshing.value = false
        refreshPromise.value = null
      }
    })()

    return refreshPromise.value
  }

  /**
   * Ожидание завершения текущего обновления токена
   * Полезно для очереди запросов
   */
  const waitForAdminTokenRefresh = (): Promise<string | null> | null => {
    if (refreshPromise.value) {
      return refreshPromise.value
    }
    return null
  }

  return {
    refreshAdminToken,
    waitForAdminTokenRefresh,
    isRefreshing: computed(() => isRefreshing.value),
  }
}

